import { FC, useState, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import {
	useGetEvents,
	useCreateEvent,
	useUpdateEvent,
	useDeleteEvent,
} from "../../hooks/useEvents";
import {
	EventRequest,
	EventResult,
	EventKPI,
	Event,
	ValidationType,
	CompletionRequirement,
} from "../../domain/event.interface";
import { useUserStore } from "../../stores/user.store";
import { PhotoConfigSection } from "./components/PhotoConfigSection";
import { FormConfigSection } from "./components/FormConfigSection";
import { CompoundConfigSection } from "./components/CompoundConfigSection";
import { MobileCardSection } from "./components/MobileCardSection";

const EventsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingEventId, setEditingEventId] = useState<string | null>(null);
	const [editingResultIndex, setEditingResultIndex] = useState<number | null>(
		null,
	);
	const [editingDashboardKPIIndex, setEditingDashboardKPIIndex] = useState<
		number | null
	>(null);
	const [editingProfileKPIIndex, setEditingProfileKPIIndex] = useState<
		number | null
	>(null);
	const [eventScope, setEventScope] = useState<"shared" | "account">("shared");
	const [formData, setFormData] = useState<EventRequest>({
		name: "",
		verificationType: "Manual",
		validationType: ValidationType.TEST_RESULT_MANUAL_INPUT,
		requireMobileValidation: false,
		cupsAndAnalytes: false,
		eventResults: [],
		dashboardKPIs: [],
		profileStatisticsKPIs: [],
		photoConfig: undefined,
		formConfig: undefined,
		compoundConfig: undefined,
		videoConfig: undefined,
		isSubEvent: false,
		accountId: undefined,
		notificationMetadata: undefined,
		mobileCard: undefined,
	});
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});

	// Event Results states
	const [newEventResult, setNewEventResult] = useState<EventResult>({
		id: "",
		display: "",
		tamperedDisplayText: "",
		shortName: "",
		displayColor: "#1976d2",
		allowForEntrySelection: false,
		allowForEditionWithOutPerm: false,
		validResult: false,
		disenrollmentJobStatus: false,
		requireConfirmation: false,
		missEntry: false,
		showAnalytes: false,
	});

	// KPI states
	const [newDashboardKPI, setNewDashboardKPI] = useState<EventKPI>({
		name: "",
		url: "",
		active: true,
		position: 0,
		variables: [],
	});
	const [newProfileKPI, setNewProfileKPI] = useState<EventKPI>({
		name: "",
		url: "",
		active: true,
		position: 0,
		variables: [],
	});
	const [newVariable, setNewVariable] = useState<{
		name: string;
		value: string;
		useDropdown: boolean;
	}>({
		name: "",
		value: "",
		useDropdown: false,
	});

	const { data: events, isLoading, error, refetch } = useGetEvents();
	const createMutation = useCreateEvent();
	const updateMutation = useUpdateEvent();
	const deleteMutation = useDeleteEvent();

	const defaultCompoundConfig = () => ({
		steps: [
			{ order: 1, eventId: "", required: true },
			{ order: 2, eventId: "", required: true },
		],
		allowPartialSubmission: false,
		completionRequirement: CompletionRequirement.ALL_REQUIRED,
	});

	const handleValidationTypeChange = (newType: ValidationType) => {
		const updates: Partial<EventRequest> = {
			validationType: newType,
			photoConfig: undefined,
			formConfig: undefined,
			compoundConfig: undefined,
			videoConfig: undefined,
		};

		if (newType === ValidationType.PHOTOS) {
			updates.photoConfig = formData.photoConfig || {
				minPhotos: 1,
				maxPhotos: 1,
			};
		} else if (newType === ValidationType.COMPOUND) {
			updates.compoundConfig =
				formData.compoundConfig || defaultCompoundConfig();
		} else if (newType === ValidationType.WATCH_VIDEO) {
			updates.videoConfig = formData.videoConfig || {
				videoUrl: "",
				duration: 0,
				title: "",
				minWatchPercentage: 100,
				allowSkip: false,
			};
		} else if (newType === ValidationType.CAPTURE_VIDEO) {
			updates.videoConfig = formData.videoConfig || {
				maxDuration: 60,
				allowRetake: true,
				uploadFormat: "mp4",
			};
		} else if (newType === ValidationType.FORM) {
			updates.formConfig = formData.formConfig || {
				surveySchema: {},
				surveySchemaVersion: "",
			};
		}

		setFormData({ ...formData, ...updates });
		setFormErrors({});
	};

	const validateForm = useCallback((): boolean => {
		const errors: Record<string, string> = {};

		if (!formData.name.trim()) {
			errors.name = "Event name is required";
		}

		if (
			formData.validationType === ValidationType.PHOTOS &&
			formData.photoConfig
		) {
			const { minPhotos, maxPhotos } = formData.photoConfig;
			if (maxPhotos < minPhotos) {
				errors.maxPhotos = "Max photos must be ≥ min photos";
			}
			if (maxPhotos > 20) {
				errors.maxPhotos = "Max photos cannot exceed 20";
			}
			if (minPhotos < 1) {
				errors.minPhotos = "Min photos must be at least 1";
			}
		}

		if (
			formData.defaultSubmissionWindow &&
			formData.defaultSubmissionWindow.duration < 1
		) {
			errors.duration = "Duration must be positive";
		}

		if (formData.validationType === ValidationType.FORM) {
			if (
				!formData.formConfig?.surveySchema ||
				Object.keys(formData.formConfig.surveySchema).length === 0
			) {
				errors.formConfig = "Survey form must have at least one question";
			}
		}

		if (formData.validationType === ValidationType.COMPOUND) {
			if (
				!formData.compoundConfig ||
				formData.compoundConfig.steps.length < 2
			) {
				errors.compoundSteps = "Minimum 2 steps required";
			} else {
				const emptySteps = formData.compoundConfig.steps.some(
					(s) => !s.eventId,
				);
				if (emptySteps) {
					errors.compoundSteps = "All steps must have an event selected";
				}
				const eventIds = formData.compoundConfig.steps
					.map((s) => s.eventId)
					.filter(Boolean);
				if (new Set(eventIds).size !== eventIds.length) {
					errors.compoundSteps = "Duplicate events are not allowed";
				}
			}
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	}, [formData]);

	const handleCreateEvent = async () => {
		if (!validateForm()) return;
		try {
			if (editingEventId) {
				// Update existing event
				await updateMutation.mutateAsync({
					eventId: editingEventId,
					event: {
						...formData,
						updatedBy: userInfo?.id || "",
					},
				});
			} else {
				// Create new event
				await createMutation.mutateAsync({
					...formData,
					createdBy: userInfo?.id || "",
				});
			}
			setShowCreateForm(false);
			setEditingEventId(null);
			setEditingResultIndex(null);
			setFormErrors({});
			setEventScope("shared");
			setFormData({
				name: "",
				verificationType: "Manual",
				validationType: ValidationType.TEST_RESULT_MANUAL_INPUT,
				requireMobileValidation: false,
				cupsAndAnalytes: false,
				eventResults: [],
				dashboardKPIs: [],
				profileStatisticsKPIs: [],
				photoConfig: undefined,
				formConfig: undefined,
				compoundConfig: undefined,
				videoConfig: undefined,
				isSubEvent: false,
				accountId: undefined,
				notificationMetadata: undefined,
				mobileCard: undefined,
			});
			setNewEventResult({
				id: "",
				display: "",
				tamperedDisplayText: "",
				shortName: "",
				displayColor: "#1976d2",
				allowForEntrySelection: false,
				allowForEditionWithOutPerm: false,
				validResult: false,
				disenrollmentJobStatus: false,
				requireConfirmation: false,
				missEntry: false,
				showAnalytes: false,
			});
			refetch();
		} catch (err) {
			const errorMessages =
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(err as any)?.message || (err as any)?.error || "An error occurred";
			const message = Array.isArray(errorMessages)
				? errorMessages.join(", ")
				: String(errorMessages);
			setFormErrors({ api: message });
		}
	};

	const handleEditEvent = (event: Event) => {
		setEditingEventId(event._id);
		setFormErrors({});
		setEventScope(event.accountId ? "account" : "shared");

		// Variables are already plain strings, no mapping needed
		setFormData({
			name: event.name,
			verificationType: event.verificationType,
			validationType: event.validationType,
			requireMobileValidation: event.requireMobileValidation || false,
			cupsAndAnalytes: event.cupsAndAnalytes || false,
			eventResults: event.eventResults || [],
			dashboardKPIs: event.dashboardKPIs || [],
			profileStatisticsKPIs: event.profileStatisticsKPIs || [],
			photoConfig: event.photoConfig,
			formConfig: event.formConfig,
			compoundConfig: event.compoundConfig,
			videoConfig: event.videoConfig,
			isSubEvent: event.isSubEvent || false,
			defaultSubmissionWindow: event.defaultSubmissionWindow,
			accountId: event.accountId || undefined,
			notificationMetadata: event.notificationMetadata,
			mobileCard: event.mobileCard,
		});
		setShowCreateForm(true);
	};

	const handleDeleteEvent = async (eventId: string) => {
		if (window.confirm("Are you sure you want to delete this event?")) {
			try {
				await deleteMutation.mutateAsync(eventId);
				refetch();
			} catch (err) {
				// Handle error silently or show notification
			}
		}
	};

	const handleCancelEdit = () => {
		setShowCreateForm(false);
		setEditingEventId(null);
		setEditingResultIndex(null);
		setFormErrors({});
		setEventScope("shared");
		setFormData({
			name: "",
			verificationType: "Manual",
			validationType: ValidationType.TEST_RESULT_MANUAL_INPUT,
			requireMobileValidation: false,
			cupsAndAnalytes: false,
			eventResults: [],
			dashboardKPIs: [],
			profileStatisticsKPIs: [],
			photoConfig: undefined,
			formConfig: undefined,
			compoundConfig: undefined,
			videoConfig: undefined,
			isSubEvent: false,
			accountId: undefined,
			notificationMetadata: undefined,
		});
		setNewEventResult({
			id: "",
			display: "",
			tamperedDisplayText: "",
			shortName: "",
			displayColor: "#1976d2",
			allowForEntrySelection: false,
			allowForEditionWithOutPerm: false,
			validResult: false,
			disenrollmentJobStatus: false,
			requireConfirmation: false,
			missEntry: false,
			showAnalytes: false,
		});
	};

	const addEventResult = () => {
		if (newEventResult.id.trim() && newEventResult.display.trim()) {
			if (editingResultIndex !== null) {
				// Update existing result
				const updatedResults = [...(formData.eventResults || [])];
				updatedResults[editingResultIndex] = { ...newEventResult };
				setFormData({
					...formData,
					eventResults: updatedResults,
				});
				setEditingResultIndex(null);
			} else {
				// Add new result
				setFormData({
					...formData,
					eventResults: [
						...(formData.eventResults || []),
						{ ...newEventResult },
					],
				});
			}
			setNewEventResult({
				id: "",
				display: "",
				tamperedDisplayText: "",
				shortName: "",
				displayColor: "#1976d2",
				allowForEntrySelection: false,
				allowForEditionWithOutPerm: false,
				validResult: false,
				disenrollmentJobStatus: false,
				requireConfirmation: false,
				missEntry: false,
				showAnalytes: false,
			});
		}
	};

	const editEventResult = (index: number) => {
		const result = formData.eventResults?.[index];
		if (result) {
			setNewEventResult({ ...result });
			setEditingResultIndex(index);
		}
	};

	const removeEventResult = (index: number) => {
		setFormData({
			...formData,
			eventResults: formData.eventResults?.filter((_, i) => i !== index) || [],
		});
	};

	const addKPI = (type: "dashboard" | "profile") => {
		const newKPI = type === "dashboard" ? newDashboardKPI : newProfileKPI;
		const setter = type === "dashboard" ? setNewDashboardKPI : setNewProfileKPI;
		const editingIndex =
			type === "dashboard" ? editingDashboardKPIIndex : editingProfileKPIIndex;
		const setEditingIndex =
			type === "dashboard"
				? setEditingDashboardKPIIndex
				: setEditingProfileKPIIndex;

		if (newKPI.name.trim() && newKPI.url.trim()) {
			const field =
				type === "dashboard" ? "dashboardKPIs" : "profileStatisticsKPIs";

			if (editingIndex !== null) {
				// Update existing KPI
				const updatedKPIs = [...(formData[field] || [])];
				updatedKPIs[editingIndex] = { ...newKPI };
				setFormData({
					...formData,
					[field]: updatedKPIs,
				});
				setEditingIndex(null);
			} else {
				// Add new KPI
				setFormData({
					...formData,
					[field]: [...(formData[field] || []), { ...newKPI }],
				});
			}

			setter({
				name: "",
				url: "",
				active: true,
				position: 0,
				variables: [],
			});
		}
	};

	const editKPI = (type: "dashboard" | "profile", index: number) => {
		const field =
			type === "dashboard" ? "dashboardKPIs" : "profileStatisticsKPIs";
		const kpi = formData[field]?.[index];
		const setter = type === "dashboard" ? setNewDashboardKPI : setNewProfileKPI;
		const setEditingIndex =
			type === "dashboard"
				? setEditingDashboardKPIIndex
				: setEditingProfileKPIIndex;

		if (kpi) {
			setter({ ...kpi });
			setEditingIndex(index);
		}
	};

	const cancelEditKPI = (type: "dashboard" | "profile") => {
		const setter = type === "dashboard" ? setNewDashboardKPI : setNewProfileKPI;
		const setEditingIndex =
			type === "dashboard"
				? setEditingDashboardKPIIndex
				: setEditingProfileKPIIndex;

		setter({
			name: "",
			url: "",
			active: true,
			position: 0,
			variables: [],
		});
		setEditingIndex(null);
	};

	const removeKPI = (type: "dashboard" | "profile", index: number) => {
		const field =
			type === "dashboard" ? "dashboardKPIs" : "profileStatisticsKPIs";
		setFormData({
			...formData,
			[field]: formData[field]?.filter((_, i) => i !== index) || [],
		});
	};

	if (isLoading) return <Typography>Loading...</Typography>;
	if (error) return <Typography color="error">Error loading events</Typography>;

	return (
		<Box sx={{ padding: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}
			>
				<Typography variant="h4" component="h1">
					Events
				</Typography>
				<Button
					variant="contained"
					color="primary"
					onClick={() => {
						if (showCreateForm) {
							handleCancelEdit();
						} else {
							setShowCreateForm(true);
						}
					}}
				>
					{showCreateForm ? "Cancel" : "Create Event"}
				</Button>
			</Box>

			{/* Create Form */}
			{showCreateForm && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" sx={{ mb: 2 }}>
							{editingEventId ? "Edit Event" : "Create New Event"}
						</Typography>

						<Grid container spacing={3}>
							{/* Basic Event Information */}
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Event Name"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
									required
									error={!!formErrors.name}
									helperText={formErrors.name}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Verification Type</InputLabel>
									<Select
										value={formData.verificationType}
										label="Verification Type"
										onChange={(e) =>
											setFormData({
												...formData,
												verificationType: e.target.value as
													| "Manual"
													| "Automatic",
											})
										}
									>
										<MenuItem value="Manual">Manual</MenuItem>
										<MenuItem value="Automatic">Automatic</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth>
									<InputLabel>Validation Type</InputLabel>
									<Select
										value={formData.validationType}
										label="Validation Type"
										onChange={(e) =>
											handleValidationTypeChange(
												e.target.value as ValidationType,
											)
										}
									>
										<MenuItem value={ValidationType.TEST_RESULT_MANUAL_INPUT}>
											Test Result Manual Input
										</MenuItem>
										<MenuItem value={ValidationType.GENERIC_MANUAL_INPUT}>
											Generic Manual Input
										</MenuItem>
										<MenuItem value={ValidationType.PHOTOS}>Photos</MenuItem>
										<MenuItem value={ValidationType.FORM}>Form</MenuItem>
										<MenuItem value={ValidationType.ATTENDANCE}>
											Attendance
										</MenuItem>
										<MenuItem value={ValidationType.COMPOUND}>
											Compound (Multi-Step)
										</MenuItem>
										<MenuItem value={ValidationType.WATCH_VIDEO}>
											Watch Video
										</MenuItem>
										<MenuItem value={ValidationType.CAPTURE_VIDEO}>
											Capture Video
										</MenuItem>
									</Select>
								</FormControl>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ mt: 0.5, display: "block" }}
								>
									How will this event be validated?
								</Typography>
							</Grid>

							{/* Conditional Config Sections */}
							{formData.validationType === ValidationType.PHOTOS &&
								formData.photoConfig && (
									<PhotoConfigSection
										photoConfig={formData.photoConfig}
										onChange={(config) =>
											setFormData({ ...formData, photoConfig: config })
										}
										errors={formErrors}
									/>
								)}

							{formData.validationType === ValidationType.FORM &&
								formData.formConfig && (
									<FormConfigSection
										formConfig={formData.formConfig}
										onChange={(config) =>
											setFormData({ ...formData, formConfig: config })
										}
										errors={formErrors}
										userId={userInfo?.id}
									/>
								)}

							{formData.validationType === ValidationType.COMPOUND &&
								formData.compoundConfig && (
									<CompoundConfigSection
										compoundConfig={formData.compoundConfig}
										onChange={(config) =>
											setFormData({ ...formData, compoundConfig: config })
										}
										availableEvents={events || []}
										eventsLoading={isLoading}
										errors={formErrors}
									/>
								)}

							{/* Watch Video Config */}
							{formData.validationType === ValidationType.WATCH_VIDEO &&
								formData.videoConfig && (
									<Grid item xs={12}>
										<Box
											sx={{
												border: "1px solid #e0e0e0",
												borderRadius: 1,
												p: 2,
											}}
										>
											<Typography variant="subtitle2" sx={{ mb: 2 }}>
												Watch Video Configuration
											</Typography>
											<Grid container spacing={2}>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														size="small"
														label="Video URL"
														value={formData.videoConfig.videoUrl || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	videoUrl: e.target.value,
																},
															})
														}
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														size="small"
														label="Video ID"
														value={formData.videoConfig.videoId || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	videoId: e.target.value,
																},
															})
														}
													/>
												</Grid>
												<Grid item xs={12} sm={4}>
													<TextField
														fullWidth
														size="small"
														label="Title"
														required
														value={formData.videoConfig.title || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	title: e.target.value,
																},
															})
														}
													/>
												</Grid>
												<Grid item xs={12} sm={4}>
													<TextField
														fullWidth
														size="small"
														type="number"
														label="Duration (seconds)"
														required
														value={formData.videoConfig.duration || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	duration: Number(e.target.value),
																},
															})
														}
														inputProps={{ min: 1 }}
													/>
												</Grid>
												<Grid item xs={12} sm={4}>
													<TextField
														fullWidth
														size="small"
														type="number"
														label="Min Watch % (0-100)"
														value={
															formData.videoConfig.minWatchPercentage ?? 100
														}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	minWatchPercentage: Number(e.target.value),
																},
															})
														}
														inputProps={{ min: 0, max: 100 }}
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														size="small"
														label="Description"
														value={formData.videoConfig.description || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	description: e.target.value,
																},
															})
														}
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														size="small"
														label="Thumbnail URL"
														value={formData.videoConfig.thumbnailUrl || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	thumbnailUrl: e.target.value,
																},
															})
														}
													/>
												</Grid>
												<Grid item xs={12}>
													<FormControlLabel
														control={
															<Switch
																size="small"
																checked={
																	formData.videoConfig.allowSkip || false
																}
																onChange={(e) =>
																	setFormData({
																		...formData,
																		videoConfig: {
																			...(formData.videoConfig ?? {}),
																			allowSkip: e.target.checked,
																		},
																	})
																}
															/>
														}
														label="Allow Skip"
													/>
												</Grid>
											</Grid>
										</Box>
									</Grid>
								)}

							{/* Capture Video Config */}
							{formData.validationType === ValidationType.CAPTURE_VIDEO &&
								formData.videoConfig && (
									<Grid item xs={12}>
										<Box
											sx={{
												border: "1px solid #e0e0e0",
												borderRadius: 1,
												p: 2,
											}}
										>
											<Typography variant="subtitle2" sx={{ mb: 2 }}>
												Capture Video Configuration
											</Typography>
											<Grid container spacing={2}>
												<Grid item xs={12} sm={4}>
													<TextField
														fullWidth
														size="small"
														type="number"
														label="Max Duration (seconds)"
														required
														value={formData.videoConfig.maxDuration || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	maxDuration: Number(e.target.value),
																},
															})
														}
														inputProps={{ min: 1, max: 300 }}
														helperText="Max 300 seconds (5 minutes)"
													/>
												</Grid>
												<Grid item xs={12} sm={4}>
													<TextField
														fullWidth
														size="small"
														type="number"
														label="Min Duration (seconds)"
														value={formData.videoConfig.minDuration || ""}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	minDuration:
																		Number(e.target.value) || undefined,
																},
															})
														}
														inputProps={{ min: 0 }}
													/>
												</Grid>
												<Grid item xs={12} sm={4}>
													<TextField
														fullWidth
														size="small"
														label="Upload Format"
														value={formData.videoConfig.uploadFormat || "mp4"}
														onChange={(e) =>
															setFormData({
																...formData,
																videoConfig: {
																	...(formData.videoConfig ?? {}),
																	uploadFormat: e.target.value,
																},
															})
														}
													/>
												</Grid>
												<Grid item xs={12}>
													<FormControlLabel
														control={
															<Switch
																size="small"
																checked={
																	formData.videoConfig.allowRetake !== false
																}
																onChange={(e) =>
																	setFormData({
																		...formData,
																		videoConfig: {
																			...(formData.videoConfig ?? {}),
																			allowRetake: e.target.checked,
																		},
																	})
																}
															/>
														}
														label="Allow Retake"
													/>
												</Grid>
											</Grid>
										</Box>
									</Grid>
								)}

							{/* Event Scope Selection */}
							<Grid item xs={12}>
								<Box
									sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}
								>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>
										Event Scope
									</Typography>
									<RadioGroup
										row
										value={eventScope}
										onChange={(e) => {
											const scope = e.target.value as "shared" | "account";
											setEventScope(scope);
											if (scope === "shared") {
												setFormData({
													...formData,
													accountId: undefined,
													notificationMetadata: undefined,
												});
											} else {
												setFormData({
													...formData,
													accountId: process.env.REACT_APP_ACCOUNT_ID || "",
												});
											}
										}}
									>
										<FormControlLabel
											value="shared"
											control={<Radio size="small" />}
											label="Shared (available to all accounts)"
											disabled={!!editingEventId}
										/>
										<FormControlLabel
											value="account"
											control={<Radio size="small" />}
											label="Account-only (scoped to this account)"
											disabled={!!editingEventId}
										/>
									</RadioGroup>
									{editingEventId && (
										<Typography variant="caption" color="text.secondary">
											Event scope cannot be changed after creation.
										</Typography>
									)}
								</Box>
							</Grid>

							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Switch
											checked={formData.requireMobileValidation}
											onChange={(e) => {
												const checked = e.target.checked;
												setFormData({
													...formData,
													requireMobileValidation: checked,
													// Clear notification metadata if mobile validation is disabled
													notificationMetadata: checked
														? formData.notificationMetadata
														: undefined,
												});
											}}
										/>
									}
									label="Require Mobile Validation"
								/>
								<FormControlLabel
									control={
										<Switch
											checked={formData.cupsAndAnalytes}
											onChange={(e) =>
												setFormData({
													...formData,
													cupsAndAnalytes: e.target.checked,
												})
											}
										/>
									}
									label="Cups and Analytes"
								/>
								<FormControlLabel
									control={
										<Switch
											checked={formData.isSubEvent || false}
											onChange={(e) =>
												setFormData({
													...formData,
													isSubEvent: e.target.checked,
												})
											}
										/>
									}
									label="Compound-only (cannot be scheduled as standalone event)"
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", ml: 6 }}
								>
									Check this if the event should ONLY be used within compound
									workflows. Leave unchecked if the event can be scheduled
									standalone AND used in compounds.
								</Typography>
							</Grid>

							{/* Notification Metadata — only for account-scoped events with mobile validation */}
							{eventScope === "account" && formData.requireMobileValidation && (
								<Grid item xs={12}>
									<Box
										sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}
									>
										<Typography variant="subtitle2" sx={{ mb: 2 }}>
											Notification Configuration
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block", mb: 2 }}
										>
											Configure notification messages that will be sent to
											participants. These messages are also used by the AI when
											generating Smart Programs.
										</Typography>
										<Grid container spacing={2}>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													size="small"
													label="Notification Title"
													value={
														formData.notificationMetadata?.notificationTitle ||
														""
													}
													onChange={(e) =>
														setFormData({
															...formData,
															notificationMetadata: {
																...formData.notificationMetadata,
																notificationTitle: e.target.value || undefined,
															},
														})
													}
													inputProps={{ maxLength: 100 }}
													helperText="Max 100 characters"
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													size="small"
													label="Notification Message"
													value={
														formData.notificationMetadata
															?.notificationMessage || ""
													}
													onChange={(e) =>
														setFormData({
															...formData,
															notificationMetadata: {
																...formData.notificationMetadata,
																notificationMessage:
																	e.target.value || undefined,
															},
														})
													}
													inputProps={{ maxLength: 500 }}
													helperText="Max 500 characters"
													multiline
													rows={2}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													size="small"
													label="Reminder Title"
													value={
														formData.notificationMetadata?.reminderTitle || ""
													}
													onChange={(e) =>
														setFormData({
															...formData,
															notificationMetadata: {
																...formData.notificationMetadata,
																reminderTitle: e.target.value || undefined,
															},
														})
													}
													inputProps={{ maxLength: 100 }}
													helperText="Max 100 characters"
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													size="small"
													label="Reminder Message"
													value={
														formData.notificationMetadata?.reminderMessage || ""
													}
													onChange={(e) =>
														setFormData({
															...formData,
															notificationMetadata: {
																...formData.notificationMetadata,
																reminderMessage: e.target.value || undefined,
															},
														})
													}
													inputProps={{ maxLength: 500 }}
													helperText="Max 500 characters"
													multiline
													rows={2}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													size="small"
													label="Activity Description"
													value={
														formData.notificationMetadata
															?.activityDescription || ""
													}
													onChange={(e) =>
														setFormData({
															...formData,
															notificationMetadata: {
																...formData.notificationMetadata,
																activityDescription:
																	e.target.value || undefined,
															},
														})
													}
													inputProps={{ maxLength: 1000 }}
													helperText="Describe the activity for AI context (max 1000 chars)"
													multiline
													rows={2}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<TextField
													fullWidth
													size="small"
													label="Timing Guidance"
													value={
														formData.notificationMetadata?.timingGuidance || ""
													}
													onChange={(e) =>
														setFormData({
															...formData,
															notificationMetadata: {
																...formData.notificationMetadata,
																timingGuidance: e.target.value || undefined,
															},
														})
													}
													inputProps={{ maxLength: 500 }}
													helperText="e.g., 'Should be done in the morning before breakfast'"
													multiline
													rows={2}
												/>
											</Grid>
										</Grid>
									</Box>
								</Grid>
							)}

							{/* Default Submission Window */}
							<Grid item xs={12}>
								<Box
									sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}
								>
									<FormControlLabel
										control={
											<Switch
												checked={!!formData.defaultSubmissionWindow}
												onChange={(e) => {
													if (e.target.checked) {
														setFormData({
															...formData,
															defaultSubmissionWindow: {
																duration: 24,
																unit: "hours",
															},
														});
													} else {
														setFormData({
															...formData,
															defaultSubmissionWindow: undefined,
														});
													}
												}}
											/>
										}
										label="Default Submission Window"
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", ml: 6, mb: 1 }}
									>
										Set a default time window for completing this event after it
										is scheduled.
									</Typography>
									{formData.defaultSubmissionWindow &&
										(() => {
											const dsw = formData.defaultSubmissionWindow;
											return (
												<Grid container spacing={2} sx={{ mt: 1 }}>
													<Grid item xs={4}>
														<TextField
															fullWidth
															size="small"
															type="number"
															label="Duration"
															value={dsw.duration}
															onChange={(e) =>
																setFormData({
																	...formData,
																	defaultSubmissionWindow: {
																		...dsw,
																		duration: Number(e.target.value),
																	},
																})
															}
															inputProps={{ min: 1 }}
															error={!!formErrors.duration}
															helperText={formErrors.duration}
														/>
													</Grid>
													<Grid item xs={4}>
														<FormControl fullWidth size="small">
															<InputLabel>Unit</InputLabel>
															<Select
																value={dsw.unit}
																label="Unit"
																onChange={(e) =>
																	setFormData({
																		...formData,
																		defaultSubmissionWindow: {
																			...dsw,
																			unit: e.target.value,
																		},
																	})
																}
															>
																<MenuItem value="minutes">Minutes</MenuItem>
																<MenuItem value="hours">Hours</MenuItem>
																<MenuItem value="days">Days</MenuItem>
															</Select>
														</FormControl>
													</Grid>
													<Grid item xs={4}>
														<TextField
															fullWidth
															size="small"
															label="Reason (optional)"
															value={dsw.reason || ""}
															onChange={(e) =>
																setFormData({
																	...formData,
																	defaultSubmissionWindow: {
																		...dsw,
																		reason: e.target.value || undefined,
																	},
																})
															}
														/>
													</Grid>
												</Grid>
											);
										})()}
								</Box>
							</Grid>

							{/* Mobile Card — collapsible, optional, controls how the
							    event renders in the mobile day-event list */}
							<MobileCardSection
								mobileCard={formData.mobileCard}
								onChange={(mobileCard) =>
									setFormData({ ...formData, mobileCard })
								}
							/>

							{/* Event Results Section */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 2 }}>
									Event Results {editingResultIndex !== null && "(Editing)"}
								</Typography>

								<Grid container spacing={2} sx={{ mb: 2 }}>
									<Grid item xs={12} sm={6} md={2}>
										<TextField
											fullWidth
											size="small"
											label="ID *"
											value={newEventResult.id}
											onChange={(e) =>
												setNewEventResult({
													...newEventResult,
													id: e.target.value,
												})
											}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={2}>
										<TextField
											fullWidth
											size="small"
											label="Display *"
											value={newEventResult.display}
											onChange={(e) =>
												setNewEventResult({
													...newEventResult,
													display: e.target.value,
												})
											}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={2}>
										<TextField
											fullWidth
											size="small"
											label="Short Name"
											value={newEventResult.shortName || ""}
											onChange={(e) =>
												setNewEventResult({
													...newEventResult,
													shortName: e.target.value,
												})
											}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={2}>
										<TextField
											fullWidth
											size="small"
											label="Invalid Display Text"
											placeholder="Override text for invalid samples"
											value={newEventResult.tamperedDisplayText || ""}
											onChange={(e) =>
												setNewEventResult({
													...newEventResult,
													tamperedDisplayText: e.target.value,
												})
											}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={1.5}>
										<TextField
											fullWidth
											size="small"
											label="Color Picker"
											type="color"
											value={newEventResult.displayColor || "#1976d2"}
											onChange={(e) =>
												setNewEventResult({
													...newEventResult,
													displayColor: e.target.value,
												})
											}
											InputLabelProps={{ shrink: true }}
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={1.5}>
										<TextField
											fullWidth
											size="small"
											label="Hex Code"
											placeholder="#1976d2"
											value={newEventResult.displayColor || "#1976d2"}
											onChange={(e) => {
												const value = e.target.value;
												// Validate hex color format
												if (value === "" || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
													setNewEventResult({
														...newEventResult,
														displayColor: value,
													});
												}
											}}
											InputLabelProps={{ shrink: true }}
											inputProps={{ maxLength: 7 }}
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={2}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={newEventResult.allowForEntrySelection}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															allowForEntrySelection: e.target.checked,
														})
													}
												/>
											}
											label="Entry Selection"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={1}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={newEventResult.validResult}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															validResult: e.target.checked,
														})
													}
												/>
											}
											label="Valid"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={2}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={newEventResult.allowForEditionWithOutPerm}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															allowForEditionWithOutPerm: e.target.checked,
														})
													}
												/>
											}
											label="Allow Edition"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={2}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={
														newEventResult.disenrollmentJobStatus || false
													}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															disenrollmentJobStatus: e.target.checked,
														})
													}
												/>
											}
											label="Disenrollment"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={2}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={newEventResult.requireConfirmation || false}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															requireConfirmation: e.target.checked,
														})
													}
												/>
											}
											label="Confirmation"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={2}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={newEventResult.missEntry || false}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															missEntry: e.target.checked,
														})
													}
												/>
											}
											label="Miss Entry"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={6} sm={3} md={2}>
										<FormControlLabel
											control={
												<Switch
													size="small"
													checked={newEventResult.showAnalytes || false}
													onChange={(e) =>
														setNewEventResult({
															...newEventResult,
															showAnalytes: e.target.checked,
														})
													}
													disabled={!formData.cupsAndAnalytes}
												/>
											}
											label="Show Analytes"
											labelPlacement="top"
										/>
									</Grid>
									<Grid item xs={12} sm={6} md={1}>
										<Box sx={{ display: "flex", gap: 0.5 }}>
											<IconButton
												onClick={addEventResult}
												color={
													editingResultIndex !== null ? "primary" : "default"
												}
											>
												{editingResultIndex !== null ? (
													<EditIcon />
												) : (
													<AddIcon />
												)}
											</IconButton>
											{editingResultIndex !== null && (
												<IconButton
													onClick={() => {
														setEditingResultIndex(null);
														setNewEventResult({
															id: "",
															display: "",
															tamperedDisplayText: "",
															shortName: "",
															displayColor: "#1976d2",
															allowForEntrySelection: false,
															allowForEditionWithOutPerm: false,
															validResult: false,
															disenrollmentJobStatus: false,
															requireConfirmation: false,
															missEntry: false,
															showAnalytes: false,
														});
													}}
													size="small"
												>
													<DeleteIcon />
												</IconButton>
											)}
										</Box>
									</Grid>
								</Grid>

								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
									{formData.eventResults?.map((result, index) => (
										<Chip
											key={index}
											label={`${result.id}: ${result.display}${result.shortName ? ` (${result.shortName})` : ""}`}
											sx={{
												bgcolor: result.displayColor || undefined,
												color: result.displayColor ? "white" : undefined,
											}}
											onClick={() => editEventResult(index)}
											onDelete={() => removeEventResult(index)}
											deleteIcon={<DeleteIcon />}
										/>
									))}
								</Box>
							</Grid>

							{/* Dashboard KPIs Section */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Dashboard KPIs
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
									<TextField
										size="small"
										label="KPI Name"
										value={newDashboardKPI.name}
										onChange={(e) =>
											setNewDashboardKPI({
												...newDashboardKPI,
												name: e.target.value,
											})
										}
									/>
									<TextField
										size="small"
										label="URL"
										value={newDashboardKPI.url}
										onChange={(e) =>
											setNewDashboardKPI({
												...newDashboardKPI,
												url: e.target.value,
											})
										}
									/>
									<TextField
										size="small"
										label="Position"
										type="number"
										value={newDashboardKPI.position}
										onChange={(e) =>
											setNewDashboardKPI({
												...newDashboardKPI,
												position: parseInt(e.target.value) || 0,
											})
										}
									/>
									<FormControlLabel
										control={
											<Switch
												size="small"
												checked={newDashboardKPI.active || false}
												onChange={(e) =>
													setNewDashboardKPI({
														...newDashboardKPI,
														active: e.target.checked,
													})
												}
											/>
										}
										label="Active"
									/>
									<IconButton
										onClick={() => addKPI("dashboard")}
										color={
											editingDashboardKPIIndex !== null ? "primary" : "default"
										}
									>
										{editingDashboardKPIIndex !== null ? (
											<EditIcon />
										) : (
											<AddIcon />
										)}
									</IconButton>
									{editingDashboardKPIIndex !== null && (
										<IconButton
											onClick={() => cancelEditKPI("dashboard")}
											size="small"
											color="error"
										>
											<DeleteIcon />
										</IconButton>
									)}
								</Box>

								{/* Variables Section */}
								{(newDashboardKPI.variables || []).length > 0 && (
									<Box sx={{ ml: 2, mb: 1 }}>
										<Typography variant="caption" color="text.secondary">
											Variables:
										</Typography>
										<Box
											sx={{
												display: "flex",
												flexWrap: "wrap",
												gap: 1,
												mt: 0.5,
											}}
										>
											{newDashboardKPI.variables?.map((variable, index) => (
												<Chip
													key={index}
													size="small"
													label={`${variable.name}: ${variable.value}`}
													onDelete={() => {
														const updatedVariables =
															newDashboardKPI.variables?.filter(
																(_, i) => i !== index,
															) || [];
														setNewDashboardKPI({
															...newDashboardKPI,
															variables: updatedVariables,
														});
													}}
												/>
											))}
										</Box>
									</Box>
								)}

								<Box
									sx={{
										display: "flex",
										gap: 1,
										mb: 1,
										ml: 2,
										flexWrap: "wrap",
										alignItems: "center",
									}}
								>
									<TextField
										size="small"
										label="Variable Name"
										value={newVariable.name}
										onChange={(e) =>
											setNewVariable({ ...newVariable, name: e.target.value })
										}
										sx={{ width: 150 }}
									/>
									<FormControlLabel
										control={
											<Switch
												size="small"
												checked={newVariable.useDropdown}
												onChange={(e) =>
													setNewVariable({
														...newVariable,
														useDropdown: e.target.checked,
														value: "",
													})
												}
											/>
										}
										label="Use Event Result"
									/>
									{newVariable.useDropdown ? (
										<FormControl size="small" sx={{ minWidth: 150 }}>
											<InputLabel>Event Result</InputLabel>
											<Select
												value={newVariable.value}
												onChange={(e) =>
													setNewVariable({
														...newVariable,
														value: e.target.value,
													})
												}
												label="Event Result"
											>
												{formData.eventResults?.map((result) => (
													<MenuItem key={result.id} value={result._id}>
														{result.display}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									) : (
										<TextField
											size="small"
											label="Variable Value"
											value={newVariable.value}
											onChange={(e) =>
												setNewVariable({
													...newVariable,
													value: e.target.value,
												})
											}
											sx={{ width: 150 }}
										/>
									)}
									<Button
										size="small"
										variant="outlined"
										startIcon={<AddIcon />}
										onClick={() => {
											if (newVariable.name && newVariable.value) {
												setNewDashboardKPI({
													...newDashboardKPI,
													variables: [
														...(newDashboardKPI.variables || []),
														{
															name: newVariable.name,
															value: newVariable.value,
														},
													],
												});
												setNewVariable({
													name: "",
													value: "",
													useDropdown: false,
												});
											}
										}}
										disabled={!newVariable.name || !newVariable.value}
									>
										Add Variable
									</Button>
								</Box>

								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.dashboardKPIs?.map((kpi, index) => {
										const variablesText =
											kpi.variables && kpi.variables.length > 0
												? ` [${kpi.variables.map((v) => `${v.name}:${v.value}`).join(", ")}]`
												: "";
										return (
											<Chip
												key={index}
												label={`${kpi.name} (pos: ${kpi.position || 0})${variablesText}`}
												color={kpi.active !== false ? "primary" : "default"}
												variant={kpi.active !== false ? "filled" : "outlined"}
												onClick={() => editKPI("dashboard", index)}
												onDelete={() => removeKPI("dashboard", index)}
												deleteIcon={<DeleteIcon />}
											/>
										);
									})}
								</Box>
							</Grid>

							{/* Profile Statistics KPIs Section */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Profile Statistics KPIs
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
									<TextField
										size="small"
										label="KPI Name"
										value={newProfileKPI.name}
										onChange={(e) =>
											setNewProfileKPI({
												...newProfileKPI,
												name: e.target.value,
											})
										}
									/>
									<TextField
										size="small"
										label="URL"
										value={newProfileKPI.url}
										onChange={(e) =>
											setNewProfileKPI({
												...newProfileKPI,
												url: e.target.value,
											})
										}
									/>
									<TextField
										size="small"
										label="Position"
										type="number"
										value={newProfileKPI.position}
										onChange={(e) =>
											setNewProfileKPI({
												...newProfileKPI,
												position: parseInt(e.target.value) || 0,
											})
										}
									/>
									<FormControlLabel
										control={
											<Switch
												size="small"
												checked={newProfileKPI.active || false}
												onChange={(e) =>
													setNewProfileKPI({
														...newProfileKPI,
														active: e.target.checked,
													})
												}
											/>
										}
										label="Active"
									/>
									<IconButton
										onClick={() => addKPI("profile")}
										color={
											editingProfileKPIIndex !== null ? "primary" : "default"
										}
									>
										{editingProfileKPIIndex !== null ? (
											<EditIcon />
										) : (
											<AddIcon />
										)}
									</IconButton>
									{editingProfileKPIIndex !== null && (
										<IconButton
											onClick={() => cancelEditKPI("profile")}
											size="small"
											color="error"
										>
											<DeleteIcon />
										</IconButton>
									)}
								</Box>

								{/* Variables Section */}
								{(newProfileKPI.variables || []).length > 0 && (
									<Box sx={{ ml: 2, mb: 1 }}>
										<Typography variant="caption" color="text.secondary">
											Variables:
										</Typography>
										<Box
											sx={{
												display: "flex",
												flexWrap: "wrap",
												gap: 1,
												mt: 0.5,
											}}
										>
											{newProfileKPI.variables?.map((variable, index) => (
												<Chip
													key={index}
													size="small"
													label={`${variable.name}: ${variable.value}`}
													onDelete={() => {
														const updatedVariables =
															newProfileKPI.variables?.filter(
																(_, i) => i !== index,
															) || [];
														setNewProfileKPI({
															...newProfileKPI,
															variables: updatedVariables,
														});
													}}
												/>
											))}
										</Box>
									</Box>
								)}

								<Box
									sx={{
										display: "flex",
										gap: 1,
										mb: 1,
										ml: 2,
										flexWrap: "wrap",
										alignItems: "center",
									}}
								>
									<TextField
										size="small"
										label="Variable Name"
										value={newVariable.name}
										onChange={(e) =>
											setNewVariable({ ...newVariable, name: e.target.value })
										}
										sx={{ width: 150 }}
									/>
									<FormControlLabel
										control={
											<Switch
												size="small"
												checked={newVariable.useDropdown}
												onChange={(e) =>
													setNewVariable({
														...newVariable,
														useDropdown: e.target.checked,
														value: "",
													})
												}
											/>
										}
										label="Use Event Result"
									/>
									{newVariable.useDropdown ? (
										<FormControl size="small" sx={{ minWidth: 150 }}>
											<InputLabel>Event Result</InputLabel>
											<Select
												value={newVariable.value}
												onChange={(e) =>
													setNewVariable({
														...newVariable,
														value: e.target.value,
													})
												}
												label="Event Result"
											>
												{formData.eventResults?.map((result) => (
													<MenuItem key={result.id} value={result._id}>
														{result.display}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									) : (
										<TextField
											size="small"
											label="Variable Value"
											value={newVariable.value}
											onChange={(e) =>
												setNewVariable({
													...newVariable,
													value: e.target.value,
												})
											}
											sx={{ width: 150 }}
										/>
									)}
									<Button
										size="small"
										variant="outlined"
										startIcon={<AddIcon />}
										onClick={() => {
											if (newVariable.name && newVariable.value) {
												setNewProfileKPI({
													...newProfileKPI,
													variables: [
														...(newProfileKPI.variables || []),
														{
															name: newVariable.name,
															value: newVariable.value,
														},
													],
												});
												setNewVariable({
													name: "",
													value: "",
													useDropdown: false,
												});
											}
										}}
										disabled={!newVariable.name || !newVariable.value}
									>
										Add Variable
									</Button>
								</Box>

								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.profileStatisticsKPIs?.map((kpi, index) => {
										const variablesText =
											kpi.variables && kpi.variables.length > 0
												? ` [${kpi.variables.map((v) => `${v.name}:${v.value}`).join(", ")}]`
												: "";
										return (
											<Chip
												key={index}
												label={`${kpi.name} (pos: ${kpi.position || 0})${variablesText}`}
												color={kpi.active !== false ? "primary" : "default"}
												variant={kpi.active !== false ? "filled" : "outlined"}
												onClick={() => editKPI("profile", index)}
												onDelete={() => removeKPI("profile", index)}
												deleteIcon={<DeleteIcon />}
											/>
										);
									})}
								</Box>
							</Grid>

							{formErrors.api && (
								<Grid item xs={12}>
									<Alert severity="error">{formErrors.api}</Alert>
								</Grid>
							)}

							<Grid item xs={12}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleCreateEvent}
									disabled={
										createMutation.isPending ||
										updateMutation.isPending ||
										!formData.name.trim()
									}
								>
									{createMutation.isPending || updateMutation.isPending
										? editingEventId
											? "Updating..."
											: "Creating..."
										: editingEventId
											? "Update Event"
											: "Create Event"}
								</Button>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			)}

			{/* Events List */}
			<Typography variant="h6" sx={{ mb: 2 }}>
				Existing Events
			</Typography>

			{events && events.length > 0 ? (
				<Grid container spacing={3}>
					{events.map((event) => (
						<Grid item xs={12} md={6} lg={4} key={event._id}>
							<Paper sx={{ p: 2 }}>
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "flex-start",
										mb: 1,
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography variant="h6">{event.name}</Typography>
										{event.accountId && (
											<Tooltip title="Account-scoped event">
												<LockIcon fontSize="small" color="action" />
											</Tooltip>
										)}
										{event.isSubEvent && (
											<Chip
												label="Compound-Only"
												size="small"
												color="info"
												variant="outlined"
											/>
										)}
									</Box>
									<Box>
										<IconButton
											size="small"
											onClick={() => handleEditEvent(event)}
											sx={{ mr: 0.5 }}
										>
											<EditIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											color="error"
											onClick={() => handleDeleteEvent(event._id)}
											disabled={deleteMutation.isPending}
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</Box>
								</Box>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 1 }}
								>
									ID: {event._id}
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 1 }}
								>
									Verification: {event.verificationType}
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 1 }}
								>
									Validation: {event.validationType}
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 2 }}
								>
									Mobile Validation:{" "}
									{event.requireMobileValidation ? "Yes" : "No"}
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 2 }}
								>
									Cups and Analytes: {event.cupsAndAnalytes ? "Yes" : "No"}
								</Typography>

								{event.eventResults && event.eventResults.length > 0 && (
									<Box sx={{ mb: 2 }}>
										<Typography variant="caption" display="block">
											Event Results:
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{event.eventResults.map((result, index) => (
												<Chip
													key={index}
													label={result.display}
													size="small"
													color={result.validResult ? "success" : "default"}
												/>
											))}
										</Box>
									</Box>
								)}

								{event.dashboardKPIs && event.dashboardKPIs.length > 0 && (
									<Box sx={{ mb: 1 }}>
										<Typography variant="caption" display="block">
											Dashboard KPIs:
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{event.dashboardKPIs
												.sort((a, b) => (a.position || 0) - (b.position || 0))
												.map((kpi, index) => (
													<Chip
														key={index}
														label={`${kpi.name} (${kpi.position || 0})`}
														size="small"
														color={kpi.active !== false ? "primary" : "default"}
														variant={
															kpi.active !== false ? "filled" : "outlined"
														}
													/>
												))}
										</Box>
									</Box>
								)}

								{event.profileStatisticsKPIs &&
									event.profileStatisticsKPIs.length > 0 && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" display="block">
												Profile Statistics KPIs:
											</Typography>
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{event.profileStatisticsKPIs
													.sort((a, b) => (a.position || 0) - (b.position || 0))
													.map((kpi, index) => (
														<Chip
															key={index}
															label={`${kpi.name} (${kpi.position || 0})`}
															size="small"
															color={
																kpi.active !== false ? "secondary" : "default"
															}
															variant={
																kpi.active !== false ? "filled" : "outlined"
															}
														/>
													))}
											</Box>
										</Box>
									)}

								{event.compoundConfig &&
									event.compoundConfig.steps.length > 0 && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" display="block">
												Compound Steps: {event.compoundConfig.steps.length}
											</Typography>
										</Box>
									)}

								{event.photoConfig && (
									<Box sx={{ mb: 1 }}>
										<Typography variant="caption" display="block">
											Photos: {event.photoConfig.minPhotos}-
											{event.photoConfig.maxPhotos}
										</Typography>
									</Box>
								)}

								{event.videoConfig && (
									<Box sx={{ mb: 1 }}>
										<Typography variant="caption" display="block">
											{event.validationType === ValidationType.WATCH_VIDEO
												? `Watch Video: ${event.videoConfig.title || "Untitled"} (${event.videoConfig.duration || 0}s)`
												: `Capture Video: max ${event.videoConfig.maxDuration || 0}s`}
										</Typography>
									</Box>
								)}

								<Typography variant="body2" color="text.secondary">
									Created: {new Date(event.createdAt).toLocaleDateString()}
								</Typography>
							</Paper>
						</Grid>
					))}
				</Grid>
			) : (
				<Typography color="text.secondary">
					No events found. Create your first event using the button above.
				</Typography>
			)}
		</Box>
	);
};

export default EventsComponent;
